import BasicResources from './basic-resources';

// The Resources class provides a set of static fields, one for each resource
// needed by the application. Users should generally call its initialise() method, to set
// up the environment and create all of the resources the application will use.

// In some (hopefully, rare) situations, it may be required that the environment is provided
// from a parent application. In these situations, call createResources(parentEnv) directly,
// and pass it the Resources.env object that is the environment required.
class Resources {
    // createResources() should create a static field for each resource:
    //
    //    const env = parentEnv || Resources.env;
    //
    //    Resources.colours = env.resource('Colours')
    //                           .baseUrl('@AvatarAdminBaseUrl')
    //                           .service('colours');
    //
    //
    // Application code can then access those resources like so:
    //
    //    const allColours = Resource.colours.get().send();

    static createResources(parentEnv) {
        const env = parentEnv || Resources.env;
        const BASE_URL = '@StudentReportsGatewayBaseUrl';
        const CERTIFICATES_BASE_URL = '@CertificatesGatewayBaseUrl';

        /* Filters */

        Resources.academicYear = env
            .resource('academicYear')
            .baseUrl(BASE_URL)
            .service('school/academicYear');

        Resources.hasLifetimeData = env
            .resource('hasLifetimeData')
            .baseUrl(BASE_URL)
            .service('school/has-lifetime-data');

        Resources.isAdmin = env
            .resource('isAdmin')
            .baseUrl(BASE_URL)
            .service('/school/is-admin');

        Resources.yearGroups = env
            .resource('yearGroups')
            .baseUrl(BASE_URL)
            .service('/school/yeargroups');

        Resources.allClassrooms = env
            .resource('allClassrooms')
            .baseUrl(BASE_URL)
            .service('school/classrooms');

        Resources.studentsByGroup = env
            .resource('studentsByGroup')
            .baseUrl(BASE_URL)
            .service('school/classrooms/:classId/students');

        /* Report data */

        Resources.yearGroupReport = env
            .resource('yearGroupReport')
            .baseUrl(BASE_URL)
            .service('classrooms/years/{yearGroupId}/{reportType}/{dateType}');

        Resources.teacherReport = env
            .resource('teacherReport')
            .baseUrl(BASE_URL)
            .service(
                'classrooms/years/{yearGroupId}/teachers/{teacherId}/{reportType}/{dateType}'
            );

        Resources.allClassesReport = env
            .resource('allClassesReport')
            .baseUrl(BASE_URL)
            .service('classrooms/{reportType}/{dateType}');

        Resources.singleClassReport = env
            .resource('singleClassReport')
            .baseUrl(BASE_URL)
            .service('classrooms/{classroomId}/{reportType}/{dateType}');

        Resources.byClassReport = env
            .resource('byClassReport')
            .baseUrl(BASE_URL)
            .service('classrooms/years/all/participation/by-class/{dateType}');

        Resources.studentReport = env
            .resource('studentReport')
            .baseUrl(BASE_URL)
            .service(
                'classrooms/{classroomId}/students/{userProfileId}/{reportType}/{dateType}'
            );

        Resources.studentReportCourse = env
            .resource('studentReportCourse')
            .baseUrl(BASE_URL)
            .service(
                'classrooms/{classroomId}/students/{userProfileId}/{reportType}/course/{dateType}'
            );

        Resources.top10StudentsReport = env
            .resource('top10Students')
            .baseUrl(BASE_URL)
            .service('students/participation/top-n/{dateType}');

        /* Export Functionality */

        Resources.allYearsExport = env
            .resource('allYearsExport')
            .baseUrl(BASE_URL)
            .service('{fileFormat}/{reportType}/years');

        Resources.allYearsExportKey = env
            .resource('allYearsExportKey')
            .baseUrl(BASE_URL)
            .service('{fileFormat}/{reportType}/years/{downloadKey}');

        Resources.yearGroupTeacherExport = env
            .resource('yearGroupTeacherExport')
            .baseUrl(BASE_URL)
            .service(
                '{fileFormat}/{reportType}/years/{yearGroupId}/teachers/{teacherId}'
            );

        Resources.yearGroupTeacherExportKey = env
            .resource('yearGroupTeacherExportKey')
            .baseUrl(BASE_URL)
            .service(
                '{fileFormat}/{reportType}/years/{yearGroupId}/teachers/{teacherId}/{downloadKey}'
            );

        Resources.allClassesExport = env
            .resource('allClassesExport')
            .baseUrl(BASE_URL)
            .service('{fileFormat}/{reportType}/classrooms');

        Resources.allClassesExportKey = env
            .resource('allClassesExportKey')
            .baseUrl(BASE_URL)
            .service('{fileFormat}/{reportType}/classrooms/{downloadKey}');

        Resources.singleClassExport = env
            .resource('singleClassExport')
            .baseUrl(BASE_URL)
            .service('{fileFormat}/{reportType}/classrooms/{classroomId}');

        Resources.singleClassExportKey = env
            .resource('singleClassExportKey')
            .baseUrl(BASE_URL)
            .service(
                '{fileFormat}/{reportType}/classrooms/{classroomId}/{downloadKey}'
            );

        Resources.studentExport = env
            .resource('studentExport')
            .baseUrl(BASE_URL)
            .service(
                'pdf/{reportType}/classrooms/{classroomId}/students/{userProfileId}'
            );

        Resources.studentExportKey = env
            .resource('studentExportKey')
            .baseUrl(BASE_URL)
            .service(
                'pdf/{reportType}/classrooms/{classroomId}/students/{userProfileId}/{downloadKey}'
            );

        Resources.skillQuestsMasteryClassroomExport = env
            .resource('skillQuestsMasteryClassroomExport')
            .baseUrl(BASE_URL)
            .service('classrooms/skill-mastery/export/this-academic-year');

        Resources.skillQuestsMasteryGroupExport = env
            .resource('skillQuestsMasteryGroupExport')
            .baseUrl(BASE_URL)
            .service(
                'classrooms/{classroomId}/skill-mastery/export/this-academic-year'
            );

        Resources.skillQuestsMasteryStudentExport = env
            .resource('skillQuestsMasteryStudentExport')
            .baseUrl(BASE_URL)
            .service(
                'classrooms/{classroomId}/students/{userProfileId}/skill-mastery/course/export/this-academic-year'
            );

        Resources.skillQuestsMasteryClassroomExportCustomDate = env
            .resource('skillQuestsMasteryClassroomExportCustomDate')
            .baseUrl(BASE_URL)
            .service('classrooms/skill-mastery/export/custom-date');

        Resources.skillQuestsMasteryGroupExportCustomDate = env
            .resource('skillQuestsMasteryGroupExportCustomDate')
            .baseUrl(BASE_URL)
            .service(
                'classrooms/{classroomId}/skill-mastery/export/custom-date'
            );

        Resources.skillQuestsMasteryStudentExportCustomDate = env
            .resource('skillQuestsMasteryStudentExportCustomDate')
            .baseUrl(BASE_URL)
            .service(
                'classrooms/{classroomId}/students/{userProfileId}/skill-mastery/course/export/custom-date'
            );

        Resources.generateDownloadReport = env
            .resource('generateDownloadReport')
            .baseUrl(BASE_URL)
            .service('zip/classrooms/{classroomId}/reports');

        Resources.downloadNewCertificates = env
            .resource('downloadNewCertificates')
            .baseUrl(CERTIFICATES_BASE_URL)
            .service('api/v1/new-certificates/export');

        Resources.downloadNewCertificatesByType = env
            .resource('downloadNewCertificatesByType')
            .baseUrl(CERTIFICATES_BASE_URL)
            .service('api/v1/new-certificates/types/{type}/export');

        Resources.downloadNewCertificatesStudent = env
            .resource('downloadNewCertificatesStudent')
            .baseUrl(CERTIFICATES_BASE_URL)
            .service('api/v1/new-certificates/students/{userId}/export');

        Resources.downloadNewCertificatesStudentByType = env
            .resource('downloadNewCertificatesStudentByType')
            .baseUrl(CERTIFICATES_BASE_URL)
            .service(
                'api/v1/new-certificates/students/{userId}/types/{type}/export'
            );

        Resources.downloadPastCertificates = env
            .resource('downloadPastCertificates')
            .baseUrl(CERTIFICATES_BASE_URL)
            .service('api/v1/certificates/export');

        Resources.downloadPastCertificatesByType = env
            .resource('downloadPastCertificatesByType')
            .baseUrl(CERTIFICATES_BASE_URL)
            .service('api/v1/certificates/types/{type}/export');

        Resources.downloadPastCertificatesStudentByType = env
            .resource('downloadPastCertificatesStudentByType')
            .baseUrl(CERTIFICATES_BASE_URL)
            .service('api/v1/certificates/students/{userProfileId}/export');

        Resources.getGemCounts = env
            .resource('getGemCounts')
            .baseUrl(CERTIFICATES_BASE_URL)
            .service('api/v1/new-certificates/gemCounts');
        /**
         * Gets Configuration Item
         * @params: key: configuration item key
         */
        Resources.getConfigurationItem = async (key) =>
            env.getConfigurationItem(key);
    }

    // Initialise the environment and resources, based on the provided
    // environment name.  We return the environment so the caller has
    // access to everything, in particular the authToken if one was
    // passed in the query parameters.

    static initialise(envName, credentials) {
        Resources.env = BasicResources.initialise(envName, credentials);

        Resources.createResources();

        return Resources.env;
    }
}

export default Resources;



// WEBPACK FOOTER //
// ./helpers/resources.js